import { Tab } from '@headlessui/react'
import { classNames } from 'lib/misc'
import { ReactNode } from 'react'

const GuidesTabs2 = () => {
  const tabs = [
    {
      title: 'Submission Guides',
      value: 'Submission Guides',
      content: (
        <div className='relative w-full h-full p-4 overflow-hidden text-white rounded-2xl md:p-10 bg-primary-purple'>
          <FilmSubmissionGuide />
        </div>
      ),
      height: 1872,
    },
    {
      title: 'Video & Audio Requirements',
      value: 'Video & Audio Requirements',
      content: (
        <div className='relative w-full h-full p-4 overflow-hidden text-white rounded-2xl md:p-10 bg-primary-purple'>
          <VideoAudioRequirements />
        </div>
      ),
      height: 1740,
    },
    {
      title: 'Artwork Poster Requirements',
      value: 'Artwork Poster Requirements',
      content: (
        <div className='relative w-full h-full p-4 overflow-hidden text-white rounded-2xl md:p-10 bg-primary-purple'>
          <ArtworkPosterRequirements />
        </div>
      ),
      height: 892,
    },
    {
      title: 'Closed Captions/Subtitle',
      value: 'Closed Captions/Subtitle',
      content: (
        <div className='relative w-full h-full p-4 overflow-hidden text-white rounded-2xl md:p-10 bg-primary-purple'>
          <CaptionsRequirements />
        </div>
      ),
      height: 1320,
    },
  ]

  return (
    <div className='relative w-full h-full overflow-y-auto'>
      <Tab.Group>
        <div className='px-4 mx-auto md:container'>
          <Tab.List className='flex flex-col items-start justify-center w-full h-auto my-8 md:items-center md:flex-row md:h-10'>
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                className={({ selected }) =>
                  classNames(
                    'capitalize px-2 md:px-5 py-1 md:py-1.5 flex items-center rounded-xl justify-start gap-2 text-sm whitespace-nowrap',
                    selected ? 'bg-primary-purple text-white' : ''
                  )
                }
              >
                {tab.title}
              </Tab>
            ))}
          </Tab.List>
        </div>
        <Tab.Panels className='mt-2'>
          {tabs.map((tab, index) => (
            <Tab.Panel key={index} className='p-3'>
              {tab.content}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  )
}
export default GuidesTabs2

const GuideContainer = ({ children }: { children: ReactNode }) => {
  return (
    <div className='container relative p-2 mx-auto my-10 md:p-6'>{children}</div>
  )
}

const FilmSubmissionGuide = () => {
  return (
    <GuideContainer>
      <h2 className='mb-4 text-2xl font-bold text-center'>
        How to Add your Feature Film/Documentary to Film Distribution
      </h2>
      <div className='mb-4'>
        <ol className='ml-4 list-decimal'>
          <li>
            Press the Create Submission button located on the top right side of the
            page.
          </li>
          <li>
            Add all the data in the fields (title, type, genre, synopsis, and
            portrait artwork 3:4).
            <span className='font-semibold'>
              {' '}
              The synopsis must be at least 200 characters, and the portrait artwork
              size should be 1575x2100 pixels.
            </span>
          </li>
          <li>
            When it’s done, press the Create Submission button located on the top
            right side of the page.
          </li>
        </ol>
      </div>

      <div className='mb-4'>
        <h3 className='mb-2 text-xl font-semibold'>Information Page</h3>
        <p className='mb-2'>
          On the information page, you must input all the required fields (full name,
          email address, address, phone number, signature). Press the continue button
          until you reach the licensing agreement where you need to sign the
          agreement. Draw your signature in the signature field and press the Agree
          button once you’re done.
        </p>
        <div className='relative max-w-lg mb-4 overflow-hidden rounded-lg shadow-lg'>
          <img src='/assets/guides/information.png' alt='' />
        </div>
        <p>
          Once the information page is done and all required fields have data, you
          will see that the Information Tab on the top right of the page has a green
          checkmark. If there are still missing data, the Information Tab will still
          be on a gray mark.
        </p>
        <div className='relative max-w-lg mb-4 overflow-hidden rounded-lg shadow-lg'>
          <img src='/assets/guides/tabs.png' alt='' />
        </div>
      </div>

      <div className='mb-4'>
        <h3 className='mb-2 text-xl font-semibold'>Metadata Page</h3>
        <p className='mb-2'>
          In the metadata page, you need to add all the required fields. Press
          continue until you reach the last part, which is adding the music cue sheet
          (optional). Once you are done with all the data needed, you can press the
          Complete button. You will see that the Metadata Tab on the top right of the
          page has a green checkmark if all the required fields have data. If there
          are still missing data, the Metadata Tab will still be on a gray mark.
        </p>
        <div className='relative max-w-lg mb-4 overflow-hidden rounded-lg shadow-lg'>
          <img src='/assets/guides/tabs-2.png' alt='' />
        </div>
      </div>

      <div className='mb-4'>
        <h3 className='mb-2 text-xl font-semibold'>Deliverables Page</h3>
        <p className='mb-2'>
          On the deliverables page, you need to upload all the required data, which
          are:
        </p>
        <ul className='mb-2 ml-4 list-disc'>
          <li>Artwork 2:3 aspect ratio</li>
          <li>Artwork 3:4 aspect ratio</li>
          <li>Artwork 16:9 aspect ratio</li>
          <li>Artwork 4:3 aspect ratio</li>
          <li>Closed caption/subtitle</li>
          <li>Trailer</li>
          <li>Master file</li>
        </ul>
        <p>
          Press the upload button in the bottom right part of the page when you
          upload the files. Once it’s done and all required files are completely
          uploaded, you will see that the Deliverables Tab on the top right of the
          page has a green checkmark. If there are still missing files, the
          Deliverables Tab will still be on a gray mark.
        </p>
        <p className='font-semibold'>Note:</p>
        <p>
          When uploading files, you need to wait until the progress bar reaches 100%.
          Please don’t refresh or close the page as it will discontinue the uploading
          of files. It will depend on your internet connection and how fast it will
          be done.
        </p>
      </div>

      <div className='mb-4'>
        <h3 className='mb-2 text-xl font-semibold'>Submission for Review</h3>
        <p className='mb-2'>
          Once the Information, Metadata, and Deliverables Tab has a green checkmark,
          you can now submit the film for review. If you see any gray marks on those
          3 tabs, it means that there are still required fields/files that need to be
          added.
        </p>
        <p>
          To submit the film for review, please go to the Deliverables page and press
          the Submit for Review button located on the bottom right of the page.
        </p>
        <p className='font-semibold'>Note:</p>
        <p>
          If there are still missing data, the submit for review button will still be
          disabled. A “What’s Missing?” button will show on the top left part, where
          you can press it to view the remaining data/files needed to be able to
          submit the film for review.
        </p>
      </div>

      <div className='mb-4'>
        <h3 className='mb-2 text-xl font-semibold'>Quality Checking</h3>
        <p>
          Your film will undergo quality checking to see if all the deliverable files
          have an issue.
        </p>
        <p>
          If there’s an issue with the assets, we will email you with details and
          information on why the assets failed in quality checking.
        </p>
        <p>
          If all the assets pass in our quality checking, we will email you regarding
          it and your film will be now ready to publish on our digitally partnered
          platforms.
        </p>
      </div>
    </GuideContainer>
  )
}

const VideoAudioRequirements = () => {
  return (
    <GuideContainer>
      <h1 className='mb-4 text-2xl font-bold text-center'>
        Video & Audio Requirements for Passing QC
      </h1>
      <p className='mb-6 text-sm text-center'>
        Submit your video in the highest quality available.
      </p>
      <div className='space-y-8'>
        <section>
          <h2 className='mb-2 text-xl font-semibold'>Required:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>Apple ProRes 422 & 422 HQ (not LT, Proxy, 4444, XQ or any other)</li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>File Types:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>.mov</li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Video:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>
              Max. 2 sec before the program starts and 2 secs after the program ends.
            </li>
            <li>
              No color bars, slate, MPAA/FBI cards, test-tone, pre-roll, or
              post-roll.
            </li>
            <li>The video must start and end on at least 1 black frame.</li>
            <li>No burned-in/embedded subtitles throughout for master video.</li>
            <li>Not interlaced.</li>
            <li>No blended frames, ghosting.</li>
            <li>No padding, letterboxing, pillar boxing.</li>
            <li>No watermark or logo burned-in/embedded throughout.</li>
            <li>
              No URLs or promos (e.g., websites, {`"coming soon,"`} release dates,
              etc.).
            </li>
            <li>SAR must have 1:1 square pixels.</li>
            <li>No Duplicate frames (occasional allowed due to mixed media).</li>
            <li>
              No squeezed/stretched videos. The correct aspect ratio should be used
              throughout.
            </li>
            <li>No cropped elements (especially text).</li>
            <li>
              No output errors (missing media info, dropouts, corrupt frames, etc.).
            </li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Resolution:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>
              Minimum width: 640 pixels; pixel width and height must be even numbers.
            </li>
            <li>
              Always upload the original source resolution. Do not upscale or
              downscale.
            </li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Frame Rates:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>
              Original framerate: 23.976, 24, 25, 29.97, 30, 50, 59.94, or 60. Do not
              convert framerate unless the source is not supported.
            </li>
            <li>Avoid duplicated frames and blended frames.</li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Audio Codec:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>PCM: minimum 48 kHz 16 Bit</li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Audio:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>Audio levels should be around a minimum of -24 LUFS.</li>
            <li>No Clipping.</li>
            <li>No Phasing.</li>
            <li>No Distortion.</li>
            <li>No Dropouts, ticks, pops, etc.</li>
            <li>The mix must be center-balanced, especially the dialogue.</li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Channel Layout:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>Stereo (Lt Rt) is required.</li>
            <li>
              Support for any additional audio stream and channel configuration
              including all surround formats.
            </li>
            <li>
              If the title is or was ever available in surround, we require surround
              plus Lt Rt tracks.
            </li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Trailer:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>
              Burned-in subtitles throughout English audio trailers are not allowed.
            </li>
            <li>
              Burned-in English subtitles are required for non-English dialogue.
            </li>
            <li>Max. 6 min, recommended 2.5 mins or less.</li>
            <li>
              To maximize exposure for your title, we recommend avoiding content that
              includes nudity, sex, language, drugs, or violence.
            </li>
          </ul>
        </section>
      </div>
    </GuideContainer>
  )
}

const ArtworkPosterRequirements = () => {
  return (
    <GuideContainer>
      <h1 className='mb-4 text-2xl font-bold text-center'>
        Artwork Poster Requirements
      </h1>
      <p className='mb-6 text-sm text-center'>
        Our artwork poster requirements are applicable to all channels.
      </p>
      <div className='space-y-8'>
        <section>
          <h2 className='mb-2 text-xl font-semibold'>Accepted Requirements:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>
              Upload 2:3 aspect ratio. Image dimensions must be at least 1400x2100
              pixels.
            </li>
            <li>
              Upload 3:4 aspect ratio. Image dimensions must be at least 1575x2100
              pixels.
            </li>
            <li>
              Upload 16:9 aspect ratio. Image dimensions must be at least 1920x1080
              pixels.
            </li>
            <li>
              Upload 4:3 aspect ratio. Image dimensions must be at least 1920x1440
              pixels.
            </li>
            <li>Include the name of the title in your artwork for all sizes.</li>
            <li>
              Match the title on the artwork to the title submitted in your metadata
              exactly.
            </li>
            <li>Ensure the title is readable at thumbnail size.</li>
            <li>
              Use the same imagery and fonts for consistency across sizes if
              possible.
            </li>
            <li>
              Provide clean artwork with only the title name in the designated text
              aspect ratios.
            </li>
            <li>
              Adjust your layout to fit text elements within the safe zone, 5% from
              each image edge.
            </li>
            <li>
              Upload higher resolution artwork that has not been upscaled to avoid
              pixelation or blurry images.
            </li>
            <li>Submit a JPEG or PNG file.</li>
          </ul>
        </section>

        <section>
          <h2 className='mb-2 text-xl font-semibold'>Not Accepted:</h2>
          <ul className='space-y-1 list-disc list-inside'>
            <li>
              Do not nest or collage several different images into your artwork.
            </li>
            <li>
              Do not use thick borders or multiple images pasted on a solid
              background.
            </li>
            <li>
              Do not submit a design with only plain title text and solid color(s) in
              the background.
            </li>
            <li>Do not stretch or pad images.</li>
            <li>
              Do not include subtext or promos (credits, billing blocks, production
              companies, actor names, taglines, URLs, quotes, streaming service
              logos, billing blocks, festival laurels, season numbers, QR codes,
              release dates, etc.).
            </li>
            <li>
              Do not include images depicting drug usage, nudity, excessive violence,
              etc. Images of weapons are permitted if they are presented discretely
              and do not point at the viewer or characters in the image.
            </li>
          </ul>
        </section>
      </div>
    </GuideContainer>
  )
}

const CaptionsRequirements = () => {
  return (
    <GuideContainer>
      <h2 className='mb-4 text-2xl font-bold text-center'>
        Captions/Subtitle Requirements
      </h2>

      <div className='space-y-8'>
        <section className='mb-4'>
          <h3 className='mb-2 text-xl font-semibold'>File Types</h3>
          <ul className='list-disc list-inside'>
            <li>.scc</li>
            <li>.srt</li>
          </ul>
        </section>

        <section className='mb-4'>
          <h3 className='mb-2 text-xl font-semibold'>Requirements</h3>
          <p className='mb-2'>
            English SDH captions are required for all titles regardless of the main
            audio language. They should not be embedded or burned-in to the master
            video file, and should be provided separately.
          </p>
          <p>
            For non-English speaking movies, English subtitles are required. They
            should not be embedded or burned-in to the master video file, and should
            be provided separately.
          </p>
        </section>

        <section className='mb-4'>
          <h3 className='mb-2 text-xl font-semibold'>Quality Requirements</h3>
          <p className='mb-2'>
            Caption/Subtitle quality is examined during QC based on how precisely the
            audio is described in the text (exactness) and how easy the text is to
            read (legibility):
          </p>
          <ul className='list-disc list-inside'>
            <li>
              <strong>Exactness:</strong> All dialogue must be transcribed exactly to
              the English SDH caption file as it sounds in the audio.
              <ul className='pl-5 list-disc list-inside'>
                <li>
                  Slang, foul language, mispronunciations, etc., must be spelled out
                  as they sound and uncensored if audible.
                </li>
              </ul>
            </li>
            <li>
              <strong>Legibility:</strong> Text must be comfortable to read and free
              of distracting typos and errors with respect to exactness.
              <ul className='pl-5 list-disc list-inside'>
                <li>Use proper punctuation.</li>
                <li>
                  Two people speaking in the same event must be denoted with hyphens:
                </li>
                <ul className='pl-10 list-disc list-inside'>
                  <li>I’m speaking first.</li>
                  <li>I’m speaking directly after you with no gap.</li>
                </ul>
              </ul>
            </li>
          </ul>
        </section>

        <section className='mb-4'>
          <h3 className='mb-2 text-xl font-semibold'>Technical Requirements</h3>
          <p className='mb-2'>
            These technical requirements have been standardized for maximum
            compatibility across all Channels and content types.
          </p>
          <ul className='list-disc list-inside'>
            <li>
              Caption/Subtitle files should be in SRT format. SCC is acceptable if
              it’s the only source available. No other file formats are supported.
            </li>
            <li>
              Caption/Subtitle files must be readable as a text file. Binary files
              are not supported.
            </li>
            <li>The caption/subtitle file must not contain blank or empty lines.</li>
            <li>Maximum 43 characters per single line.</li>
            <li>
              Dialogue from a single speaker that is less than 43 characters should
              be entered in a single line.
            </li>
            <li>If it is longer than 43 characters, break it into two lines.</li>
            <li>Maximum of two lines per caption event.</li>
            <li>
              Dialogue from a single speaker that spans more than one event can be
              bridged with an ellipsis.
            </li>
            <li>At least 66 milliseconds between different caption events.</li>
            <li>No more than 25 characters per second.</li>
            <li>
              Caption/Subtitle events must last for at least 600 milliseconds (0.6
              seconds).
            </li>
            <li>
              Caption/Subtitle events must last no longer than 8000 milliseconds (8
              seconds).
            </li>
            <li>
              Caption/Subtitle events must appear and disappear within 0.5 seconds of
              the associated audio.
            </li>
            <li>
              Atmospherics should be enclosed in parentheses (). If they are enclosed
              in {} or &lt;&gt;, they will be deleted, which may result in an empty
              line.
            </li>
            <li>
              All captions/subtitles must be complete, accurate, and synced with the
              video.
              <ul className='pl-5 list-disc list-inside'>
                <li>This includes lyrics in the opening and closing credits.</li>
              </ul>
            </li>
            <li>Correct punctuation and spelling are required.</li>
          </ul>
        </section>

        <section>
          <p>
            You can use Rev Caption for creating closed caption files for titles with
            English as the main audio language, if you don’t have one.
          </p>
        </section>
      </div>
    </GuideContainer>
  )
}
